/**
 * Display-only forecast of what opposing managers may need before the user's
 * next pick.
 *
 * The simulation is deliberately separate from recommendation scoring. It is
 * a transparent second opinion for a human reading the board: roster pressure
 * says what a team could use, while ADP says which available player is a
 * plausible selection at that pick. It never claims to know a manager's
 * personal strategy and it never changes engine inputs.
 */

import type {
  AdpRow,
  DraftState,
  LeagueConfig,
  Player,
  PlayerId,
  PlayerUniverse,
  Position,
} from './types.ts';
import { FANTASY_POSITIONS } from './types.ts';
import { adpConfidence, availabilityAt, normalCdf, type AdpConfidence } from './availability.ts';
import { computeRosterNeed } from './valuation.ts';
import { draftSlotForPick } from './draftBoard.ts';

export type DemandPressure = 'high' | 'medium';
export type OpponentDemandMode = 'market' | 'needs-only';

export interface OpponentDemandTeamInput {
  roster_id: number;
  draft_slot: number;
  team_name: string;
  player_ids: PlayerId[];
}

export interface OpponentDemandPosition {
  position: Position;
  /** Number of deterministic projected selections at this position, or null without market data. */
  projected_picks: number | null;
  /** Teams with an open starter or flex slot at this position. */
  teams_needing: number;
  team_names: string[];
  /** Null when this is roster-needs-only output or no picks are projected. */
  pressure: DemandPressure | null;
}

export interface OpponentDemandSelectedPlayer {
  player_id: PlayerId;
  name: string;
  position: Position;
  pick_no: number;
}

export interface OpponentDemandTeamForecast {
  pick_no: number;
  draft_slot: number;
  team_name: string;
  /** Top positional signals before this pick; probabilities are absent in needs-only mode. */
  likely_positions: { position: Position; probability: number | null }[];
  selected_player: OpponentDemandSelectedPlayer | null;
}

export interface OpponentDemandExposedPlayer {
  player_id: PlayerId;
  name: string;
  position: Position;
  projected_pick_no: number;
  /** Existing ADP model probability that the player is gone by the user's target. */
  gone_probability: number;
  position_projected_picks: number;
}

export interface OpponentDemandForecast {
  mode: OpponentDemandMode;
  target_pick_no: number;
  picks_before_target: number;
  /** How many of the upcoming picks had a meaningful market candidate. */
  picks_projected: number;
  /** Conservative confidence across the players actually projected. */
  confidence: AdpConfidence;
  positions: OpponentDemandPosition[];
  teams: OpponentDemandTeamForecast[];
  /** Empty in needs-only mode. */
  exposed_players: OpponentDemandExposedPlayer[];
}

export interface OpponentDemandInput {
  draft: DraftState;
  league: LeagueConfig;
  teams: OpponentDemandTeamInput[];
  universe: PlayerUniverse;
  adpIndex: ReadonlyMap<PlayerId, AdpRow>;
  /** Available IDs after synced picks, keepers, and local corrections are applied. */
  availablePlayerIds: ReadonlySet<PlayerId>;
}

interface DemandProfile {
  score: Record<Position, number>;
  open: Set<Position>;
  /** Required kicker/defense slots still open; used for late-round timing. */
  specialist_slots_open: number;
}

interface UpcomingOpponentPick {
  pick_no: number;
  draft_slot: number;
  team: OpponentDemandTeamInput | undefined;
}

interface MarketCandidate {
  player: Player;
  adp: AdpRow;
}

interface PositionScore {
  position: Position;
  score: number;
}

const MARKET_SCORE_FLOOR = 1e-5;
const SPECIALIST_POSITIONS = new Set<Position>(['K', 'DEF']);
const SPECIALIST_DISPLAY_THRESHOLD = 0.08;

/**
 * Build an explainable, deterministic opponent-demand read for the interval
 * before the user's next actionable pick. Candidates and projected rosters are
 * updated after every simulated pick, so totals cannot exceed the available
 * candidate pool and a manager's second pick sees their first projected pick.
 */
export function buildOpponentDemandForecast(input: OpponentDemandInput): OpponentDemandForecast | null {
  const upcoming = upcomingOpponentPicks(input.draft, input.league, input.teams);
  const target = targetPick(input.draft);
  if (target === null || upcoming.length === 0) return null;

  const profiles = new Map<number, DemandProfile>();
  const projectedRosters = new Map<number, PlayerId[]>();
  for (const team of input.teams) {
    profiles.set(team.draft_slot, profileFor(team.player_ids, input.league, input.universe));
    projectedRosters.set(team.draft_slot, [...team.player_ids]);
  }

  const candidatesByPosition = indexCandidates(input);
  const remaining = new Set(input.availablePlayerIds);
  const teamForecasts: OpponentDemandTeamForecast[] = [];
  const projectedPlayers: OpponentDemandSelectedPlayer[] = [];
  const confidence: AdpConfidence[] = [];
  let modelingStopped = false;

  for (const pick of upcoming) {
    const profile = profiles.get(pick.draft_slot) ?? emptyProfile();
    if (modelingStopped) {
      teamForecasts.push(needsOnlyTeamForecast(pick, profile));
      continue;
    }
    const specialistMultiplier = specialistMultiplierForPick(profile, pick.pick_no, pick.draft_slot, input.league);
    const scores = positionScores(
      profile,
      candidatesByPosition,
      remaining,
      input.draft.current_pick_no,
      pick.pick_no,
      specialistMultiplier,
    );
    const meaningful = scores.filter((entry) => entry.score >= MARKET_SCORE_FLOOR);
    const likelyPositions = meaningful.length === 0
      ? []
      : topPositions(meaningful);

    if (meaningful.length === 0) {
      teamForecasts.push(needsOnlyTeamForecast(pick, profile));
      modelingStopped = true;
      continue;
    }

    const selectedPosition = meaningful[0]!.position;
    const selected = bestCandidate(
      candidatesByPosition.get(selectedPosition) ?? [],
      remaining,
      input.draft.current_pick_no,
      pick.pick_no,
      profile.score[selectedPosition] ?? 0,
      SPECIALIST_POSITIONS.has(selectedPosition) ? specialistMultiplier : 1,
    );
    if (!selected) {
      teamForecasts.push(needsOnlyTeamForecast(pick, profile));
      modelingStopped = true;
      continue;
    }

    const selectedPlayer: OpponentDemandSelectedPlayer = {
      player_id: selected.player.player_id_sleeper,
      name: selected.player.full_name,
      position: selectedPosition,
      pick_no: pick.pick_no,
    };
    teamForecasts.push({
      pick_no: pick.pick_no,
      draft_slot: pick.draft_slot,
      team_name: pick.team?.team_name ?? `Team ${pick.draft_slot}`,
      likely_positions: likelyPositions,
      selected_player: selectedPlayer,
    });
    projectedPlayers.push(selectedPlayer);
    confidence.push(adpConfidence(selected.adp.times_drafted, selected.adp.adp));
    remaining.delete(selected.player.player_id_sleeper);

    const roster = projectedRosters.get(pick.draft_slot) ?? [];
    roster.push(selected.player.player_id_sleeper);
    projectedRosters.set(pick.draft_slot, roster);
    profiles.set(pick.draft_slot, profileFor(roster, input.league, input.universe));
  }

  const projectedByPosition = new Map<Position, number>();
  for (const player of projectedPlayers) {
    projectedByPosition.set(player.position, (projectedByPosition.get(player.position) ?? 0) + 1);
  }

  const upcomingSlots = new Set(upcoming.map((pick) => pick.draft_slot));
  const initialProfiles = new Map<number, DemandProfile>();
  for (const team of input.teams) {
    initialProfiles.set(team.draft_slot, profileFor(team.player_ids, input.league, input.universe));
  }
  const upcomingTeams = input.teams.filter((team) => upcomingSlots.has(team.draft_slot));
  const positions = buildPositionOutput(
    upcoming,
    upcomingTeams,
    initialProfiles,
    projectedByPosition,
    projectedPlayers.length > 0,
    input.league,
  );
  const mode: OpponentDemandMode = projectedPlayers.length > 0 ? 'market' : 'needs-only';

  return {
    mode,
    target_pick_no: target,
    picks_before_target: upcoming.length,
    picks_projected: projectedPlayers.length,
    confidence: mode === 'market' ? conservativeConfidence(confidence) : 'none',
    positions,
    teams: teamForecasts,
    exposed_players: mode === 'market'
      ? exposedPlayers(input, target, projectedPlayers, projectedByPosition)
      : [],
  };
}

function targetPick(draft: DraftState): number | null {
  if (draft.my_next_pick_no === null) return null;
  return draft.my_next_pick_no === draft.current_pick_no
    ? draft.my_pick_after_that
    : draft.my_next_pick_no;
}

function upcomingOpponentPicks(
  draft: DraftState,
  league: LeagueConfig,
  teams: OpponentDemandTeamInput[],
): UpcomingOpponentPick[] {
  const target = targetPick(draft);
  if (target === null || draft.my_draft_slot === null) return [];

  const startsAfterCurrent = draft.my_next_pick_no === draft.current_pick_no;
  const start = startsAfterCurrent ? draft.current_pick_no + 1 : draft.current_pick_no;
  const teamBySlot = new Map(teams.map((team) => [team.draft_slot, team]));
  const out: UpcomingOpponentPick[] = [];
  for (let pickNo = start; pickNo < target; pickNo++) {
    const draftSlot = draftSlotForPick(pickNo, league.teams, league.draft_type);
    if (draftSlot === draft.my_draft_slot) continue;
    out.push({ pick_no: pickNo, draft_slot: draftSlot, team: teamBySlot.get(draftSlot) });
  }
  return out;
}

function profileFor(roster: PlayerId[], league: LeagueConfig, universe: PlayerUniverse): DemandProfile {
  const need = computeRosterNeed(roster, league, (id) => universe.get(id)?.position ?? null);
  const open = new Set<Position>();
  for (const position of FANTASY_POSITIONS) {
    if ((need.open_starters[position] ?? 0) > 0 || need.open_flex.some((flex) => flex.count > 0 && flex.eligible.includes(position))) {
      open.add(position);
    }
  }
  const specialist_slots_open = (need.open_starters.K ?? 0) + (need.open_starters.DEF ?? 0);
  return { score: need.score, open, specialist_slots_open };
}

function emptyProfile(): DemandProfile {
  return { score: emptyPositionRecord(), open: new Set(), specialist_slots_open: 0 };
}

function emptyPositionRecord(): Record<Position, number> {
  return { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0 };
}

function indexCandidates(input: OpponentDemandInput): Map<Position, MarketCandidate[]> {
  const indexed = new Map<Position, MarketCandidate[]>();
  for (const id of input.availablePlayerIds) {
    const player = input.universe.get(id);
    const adp = input.adpIndex.get(id);
    if (!player?.position || !adp) continue;
    const list = indexed.get(player.position) ?? [];
    list.push({ player, adp });
    indexed.set(player.position, list);
  }
  for (const list of indexed.values()) {
    list.sort((a, b) => a.adp.adp - b.adp.adp || a.player.player_id_sleeper.localeCompare(b.player.player_id_sleeper));
  }
  return indexed;
}

function positionScores(
  profile: DemandProfile,
  candidatesByPosition: Map<Position, MarketCandidate[]>,
  remaining: ReadonlySet<PlayerId>,
  currentPick: number,
  targetPick: number,
  specialistMultiplier: number,
): PositionScore[] {
  return FANTASY_POSITIONS
    .map((position) => {
      const needMultiplier = 0.2 + 0.8 * (profile.score[position] ?? 0);
      const timingMultiplier = SPECIALIST_POSITIONS.has(position) ? specialistMultiplier : 1;
      const score = (candidatesByPosition.get(position) ?? [])
        .filter((candidate) => remaining.has(candidate.player.player_id_sleeper))
        .reduce((total, candidate) => total + candidateScore(candidate, currentPick, targetPick, needMultiplier * timingMultiplier), 0);
      return { position, score };
    })
    .sort((a, b) => b.score - a.score || positionOrder(a.position) - positionOrder(b.position));
}

function candidateScore(candidate: MarketCandidate, currentPick: number, targetPick: number, needMultiplier: number): number {
  return conditionalDensity(candidate.adp, targetPick, currentPick) * needMultiplier;
}

function bestCandidate(
  candidates: MarketCandidate[],
  remaining: ReadonlySet<PlayerId>,
  currentPick: number,
  targetPick: number,
  needScore: number,
  specialistMultiplier: number,
): MarketCandidate | null {
  const needMultiplier = (0.2 + 0.8 * needScore) * specialistMultiplier;
  return candidates
    .filter((candidate) => remaining.has(candidate.player.player_id_sleeper))
    .map((candidate) => ({ candidate, score: candidateScore(candidate, currentPick, targetPick, needMultiplier) }))
    .filter((entry) => entry.score >= MARKET_SCORE_FLOOR)
    .sort((a, b) => b.score - a.score || a.candidate.adp.adp - b.candidate.adp.adp || a.candidate.player.player_id_sleeper.localeCompare(b.candidate.player.player_id_sleeper))[0]?.candidate ?? null;
}

function topPositions(scores: PositionScore[]): { position: Position; probability: number }[] {
  const total = scores.reduce((sum, entry) => sum + entry.score, 0);
  if (total <= 0) return [];
  return scores
    .map((entry) => ({ position: entry.position, probability: entry.score / total }))
    .filter((entry, index) => index === 0 || entry.probability >= 0.12)
    .slice(0, 2);
}

function needsOnlyPositions(profile: DemandProfile): { position: Position; probability: null }[] {
  return FANTASY_POSITIONS
    .filter((position) => profile.open.has(position))
    .sort((a, b) => (profile.score[b] ?? 0) - (profile.score[a] ?? 0) || positionOrder(a) - positionOrder(b))
    .slice(0, 2)
    .map((position) => ({ position, probability: null }));
}

function needsOnlyTeamForecast(pick: UpcomingOpponentPick, profile: DemandProfile): OpponentDemandTeamForecast {
  return {
    pick_no: pick.pick_no,
    draft_slot: pick.draft_slot,
    team_name: pick.team?.team_name ?? `Team ${pick.draft_slot}`,
    likely_positions: needsOnlyPositions(profile),
    selected_player: null,
  };
}

function buildPositionOutput(
  upcoming: UpcomingOpponentPick[],
  upcomingTeams: OpponentDemandTeamInput[],
  profiles: Map<number, DemandProfile>,
  projectedByPosition: Map<Position, number>,
  market: boolean,
  league: LeagueConfig,
): OpponentDemandPosition[] {
  const specialistActive = upcoming.some((pick) => {
    const profile = profiles.get(pick.draft_slot) ?? emptyProfile();
    return specialistMultiplierForPick(profile, pick.pick_no, pick.draft_slot, league) >= SPECIALIST_DISPLAY_THRESHOLD;
  });

  return FANTASY_POSITIONS
    .map((position) => {
      const needingTeams = upcomingTeams.filter((team) => profiles.get(team.draft_slot)?.open.has(position));
      const projected = projectedByPosition.get(position) ?? 0;
      return {
        position,
        projected_picks: market ? projected : null,
        teams_needing: needingTeams.length,
        team_names: needingTeams.map((team) => team.team_name),
        pressure: market ? pressureFor(projected) : null,
      } satisfies OpponentDemandPosition;
    })
    .filter((position) => {
      if (SPECIALIST_POSITIONS.has(position.position) && !specialistActive) return false;
      return position.teams_needing > 0 || (position.projected_picks ?? 0) > 0;
    })
    .sort((a, b) => {
      const projectedDelta = (b.projected_picks ?? -1) - (a.projected_picks ?? -1);
      return projectedDelta || b.teams_needing - a.teams_needing || positionOrder(a.position) - positionOrder(b.position);
    });
}

/**
 * Kicker and defense slots are real roster needs, but managers usually defer
 * them until the end of the draft. Keep the market signal available while
 * applying a strong, round-aware prior against projecting them too early.
 */
function specialistMultiplierForPick(profile: DemandProfile, pickNo: number, draftSlot: number, league: LeagueConfig): number {
  const picksRemaining = picksRemainingForSlot(pickNo, draftSlot, league);
  const discretionaryPicks = picksRemaining - profile.specialist_slots_open;
  if (discretionaryPicks >= 2) return 0.01;
  if (discretionaryPicks === 1) return 0.08;
  return 1;
}

function picksRemainingForSlot(pickNo: number, draftSlot: number, league: LeagueConfig): number {
  const finalPick = league.teams * league.rounds;
  let remaining = 0;
  for (let nextPick = pickNo; nextPick <= finalPick; nextPick++) {
    if (draftSlotForPick(nextPick, league.teams, league.draft_type) === draftSlot) remaining++;
  }
  return remaining;
}

function pressureFor(projectedPicks: number): DemandPressure | null {
  if (projectedPicks >= 2) return 'high';
  if (projectedPicks === 1) return 'medium';
  return null;
}

function positionOrder(position: Position): number {
  return FANTASY_POSITIONS.indexOf(position);
}

function conditionalDensity(adp: AdpRow, pickNo: number, currentPick: number): number {
  const stdev = Math.max(adp.stdev, 1.5 + adp.adp * 0.06);
  const survived = Math.max(1e-4, 1 - normalCdf((currentPick - adp.adp) / stdev));
  const z = (pickNo - adp.adp) / stdev;
  return Math.exp(-0.5 * z * z) / (stdev * Math.sqrt(2 * Math.PI) * survived);
}

function conservativeConfidence(confidence: AdpConfidence[]): AdpConfidence {
  if (confidence.length === 0) return 'none';
  const order: AdpConfidence[] = ['none', 'low', 'medium', 'high'];
  return confidence.reduce((worst, current) => order.indexOf(current) < order.indexOf(worst) ? current : worst, 'high');
}

function exposedPlayers(
  input: OpponentDemandInput,
  target: number,
  projectedPlayers: OpponentDemandSelectedPlayer[],
  projectedByPosition: ReadonlyMap<Position, number>,
): OpponentDemandExposedPlayer[] {
  return projectedPlayers.map((projected) => {
    const adp = input.adpIndex.get(projected.player_id);
    const availability = adp
      ? availabilityAt({
          adp: adp.adp,
          stdev: adp.stdev,
          times_drafted: adp.times_drafted,
          target_pick: target,
          current_pick: input.draft.current_pick_no,
        })
      : null;
    return {
      player_id: projected.player_id,
      name: projected.name,
      position: projected.position,
      projected_pick_no: projected.pick_no,
      gone_probability: availability ? 1 - availability.probability : 0,
      position_projected_picks: projectedByPosition.get(projected.position) ?? 0,
    } satisfies OpponentDemandExposedPlayer;
  });
}
